import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import moment from 'moment';
import { MdOutlineRemoveRedEye, MdChevronLeft, MdChevronRight } from 'react-icons/md';
import { IconBaseProps } from 'react-icons/lib';
import Notice from './Notice';

import icon from '../../assets/support-icon3.svg';
import back from '../../assets/Back_Icon.svg';
import icon1 from '../../assets/icon_edit.svg';
import Delete from '../../assets/icon_delete.svg';
import fileIcon from '../../assets/fileIcon.svg';
import arr_right from '../../assets/arr_right.svg';

interface Notice {
  _id: string;
  user: string;
  category: string;
  title: string;
  content: string;
  files: string[];
  viewCount: number;
  createdAt: string;
}

interface File {
  _id: string;
  url: string;
  type: string;
  originalName: string;
  savedName: string;
}

const NoticeDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [notice, setNotice] = useState<Notice | null>(null);
  const { user } = useAuth();
  const [images, setImages] = useState<File[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEdit, setIsEdit] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const isProduction = process.env.NODE_ENV === 'production';
  const clientUrl = 'https://reborntalk.net';

  const ViewIcon = MdOutlineRemoveRedEye as unknown as React.FC<IconBaseProps>;
  const LeftIcon = MdChevronLeft as unknown as React.FC<IconBaseProps>;
  const RightIcon = MdChevronRight as unknown as React.FC<IconBaseProps>;

  const getCategoryKorean = (category: string) => {
    const categoryMap: { [key: string]: string } = {
      general: '일반',
      event: '이벤트',
      update: '업데이트',
      etc: '기타',
    };
    return categoryMap[category] || category;
  };

  const fetchNoticeDetail = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`/api/notice/${id}`);
      setNotice(response.data);

      // files 배열 가져오기
      const fileList = response.data.fileList || [];

      // 이미지 파일 필터
      const images = fileList.filter(
        (file: File) =>
          file.type?.endsWith('/jpeg') ||
          file.type?.endsWith('/jpeg') ||
          file.type?.endsWith('/png') ||
          file.type?.endsWith('/gif')
      );
      setImages(images);

      console.log('--------------images', images);

      // 이미지가 아닌 파일
      const otherFiles = fileList.filter(
        (file: File) =>
          !file.type?.endsWith('/jpg') &&
          !file.type?.endsWith('/jpeg') &&
          !file.type?.endsWith('/png') &&
          !file.type?.endsWith('/gif')
      );
      setFiles(otherFiles);

      console.log('--------------otherFiles', otherFiles);
    } catch (err) {
      setError('공지사항을 불러오는데 실패했습니다.');
      console.error('Error fetching Notice detail:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchNoticeDetail();
    }
  }, [id]);

  // 공지사항 수정
  const handleEditSuccess = async () => {
    setIsEdit(false);
    await fetchNoticeDetail();
  };

  const handleFileDownload = async (savedName: string, originalName: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/upload-files/download/${savedName}`, {
        params: {
          usedFor: 'notice',
        },
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', originalName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('파일 다운로드 실패:', err);
    }
  };

  // 이미지 슬라이드
  const handlePrevImage = () => {
    setCurrentImageIndex(prev => (prev === 0 ? images.length - 1 : prev - 1));
  };
  const handleNextImage = () => {
    setCurrentImageIndex(prev => (prev === images.length - 1 ? 0 : prev + 1));
  };

  // 공지사항 삭제
  const handleDelete = async () => {
    if (!window.confirm('해당 공지사항을 삭제하시겠습니까?')) return;

    try {
      await axios.delete(`/api/notice/${id}`);

      if (notice?.files) {
        await Promise.all(
          notice.files.map(file =>
            axios.put(`/api/upload-files/mark-deleted/${file}`, { deleted: true })
          )
        );
      }

      alert('삭제되었습니다.');
      navigate('/setting/notice');
    } catch (err) {
      console.error(err);
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  useEffect(() => {
    console.log(isProduction, 'isProduction');
  }, [isProduction]);

  return (
    <div className="qna-container">
      {!isEdit && (
        <div className="page-header">
          <img src={icon} alt="Mind Thermometer Icon" className="page-header__icon" />
          <h2 className="page-header__title">공지사항</h2>
          <p>공지사항을 확인하세요.</p>
        </div>
      )}
      <>
        {isEdit ? (
          <>
            <Notice mode="edit" onEditSuccess={handleEditSuccess} />
          </>
        ) : (
          <div className="notice-detail">
            <div className="notice-detail__header">
              <button
                onClick={() => navigate('/setting/notice')}
                className="notice-detail__back-btn"
              >
                <img src={back} alt="목록으로" />
                <p>목록으로</p>
              </button>
              <div className="notice-detail__btn-group">
                {user?.role === 'admin' && (
                  <>
                    <button onClick={() => setIsEdit(true)} className="notice-detail__edit-btn">
                      <img src={icon1} alt="수정" />
                      <p>수정</p>
                    </button>
                    <button onClick={handleDelete} className="notice-detail__delete-btn">
                      <img src={Delete} alt="삭제" />
                      <p>삭제</p>
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className="notice-detail__content">
              <div className="notice-detail__question">
                <span className="notice-detail__category">
                  {getCategoryKorean(notice?.category ?? '')}
                </span>
                <h2 className="notice-detail__title">{notice?.title}</h2>
                <div className="notice-detail__date">
                  <span>작성일 {moment(notice?.createdAt).format('YYYY-MM-DD HH:mm')}</span>
                  <div className="notice-detail__content-view-count">
                    <ViewIcon style={{ color: '#555', fontSize: '16px' }} />
                    <span>{notice?.viewCount}</span>
                  </div>
                </div>
              </div>

              {images.length > 0 && (
                <div className="notice-detail__content-images">
                  {images.length > 1 && currentImageIndex > 0 && (
                    <button
                      className="notice-detail__image-nav notice-detail__image-nav--prev"
                      onClick={handlePrevImage}
                      disabled={images.length <= 1}
                    >
                      <LeftIcon style={{ fontSize: '24px' }} />
                    </button>
                  )}

                  <div className="notice-detail__image-container">
                    <div className="notice-detail__content-image">
                      <img
                        src={
                          isProduction
                            ? `${clientUrl}${images[currentImageIndex]?.url}` // 실서버
                            : images[currentImageIndex]?.url // 개발 환경
                        }
                        alt={images[currentImageIndex]?.originalName}
                      />
                    </div>
                    {images.length > 1 && (
                      <div className="notice-detail__image-counter">
                        {currentImageIndex + 1} / {images.length}
                      </div>
                    )}
                  </div>

                  {images.length > 1 && currentImageIndex < images.length - 1 && (
                    <button
                      className="notice-detail__image-nav notice-detail__image-nav--next"
                      onClick={handleNextImage}
                      disabled={images.length <= 1}
                    >
                      <RightIcon style={{ fontSize: '24px' }} />
                    </button>
                  )}
                </div>
              )}

              <div
                className="notice-detail__content-content"
                dangerouslySetInnerHTML={{ __html: notice?.content ?? '' }}
              />

              {files.length > 0 && (
                <>
                  <div className="notice-detail__attachments">
                    {files.map(file => (
                      <button
                        key={file._id}
                        rel="noopener noreferrer"
                        className="notice-detail__file-btn"
                        onClick={() => handleFileDownload(file.savedName, file.originalName)}
                      >
                        <div>
                          <img src={fileIcon} alt="업로드 파일" />
                          <p>{file.originalName}</p>
                        </div>
                        <span className="qna-detail__file-btn-icon">↓</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </>
    </div>
  );
};

export default NoticeDetail;
